<template>
  <div class="search-input-container" >
    <!-- Mobile Full Screen Search -->
    <div
      v-if="isMobile && $mainState.isOpenSearch"
      class="fixed inset-0 bg-white z-[999]"
    >
      <!-- Header -->
      <div class="flex items-center gap-3 py-4 border-b border-slate-100  bg-slate-50 sticky top-0 shadow-sm">
        <form @submit.prevent="goSearch" class="flex-1 bg-slate-50">
          <UInput
            ref="searchInput"
            v-model="searchWord"
            type="text"
            variant="none"
            size="lg"
            placeholder="Ürün, kategori veya marka ara..."
            class="!shadow-none !border-none !ring-0 bg-slate-50"
            @input="handleInput"
            @keydown="handleKeyDown"
            role="combobox"
            aria-expanded="true"
            aria-controls="search-suggestions"
            :aria-activedescendant="selectedIndex >= 0 ? `suggestion-${selectedIndex}` : undefined"
          >
            <template #leading>
              <UIcon
                name="i-heroicons-magnifying-glass"
                class="w-5 h-5 text-slate-400"
              />
            </template>
          </UInput>
        </form>
        <UButton
          variant="ghost"
          color="gray"
          icon="i-heroicons-x-mark"
          class="flex-shrink-0"
          @click="closeSearch"
          aria-label="Aramayı kapat"
        />
      </div>

      <!-- Search Results -->
      <div id="search-suggestions" class="h-[calc(100vh-73px)] overflow-y-auto bg-white">
        <div
          v-if="isLoading"
          class="flex items-center justify-center gap-2 py-6 px-4 text-slate-500"
        >
          <UIcon name="i-heroicons-arrow-path" class="w-5 h-5 animate-spin" />
          <span class="text-sm font-medium">Aranıyor...</span>
        </div>

        <div v-else-if="searchWord && hasResults">
          <!-- Öneriler -->
          <div v-if="suggestions.length > 0" class="mb-2">
            <div class="px-4 py-2 bg-slate-50 text-xs font-medium text-slate-500 uppercase">
              Öneriler
            </div>
            <div class="divide-y divide-slate-100">
              <div
                v-for="(suggestion, index) in suggestions"
                :key="suggestion.id"
                :id="'suggestion-' + index"
                :class="[
                  'hover:bg-slate-50 active:bg-slate-100 transition-colors',
                  { 'bg-slate-50': selectedIndex === index }
                ]"
                role="option"
                :aria-selected="selectedIndex === index"
              >
                <button
                  @click="navigateToSuggestion(suggestion); closeSearch()"
                  class="w-full px-4 py-3 text-left"
                >
                  <div class="flex items-center gap-3">
                    <div v-if="(suggestion.type === 'BRAND' || suggestion.type === 'VENDOR') && suggestion.metadata?.logo" class="w-8 h-8">
                      <NuxtImg
                        :src="'cl' + suggestion.metadata.logo"
                        class="w-full h-full object-contain border rounded-full"
                        :alt="suggestion.text"
                      />
                    </div>
                    
                    <UIcon
                      v-else
                      :name="getIconForType(suggestion.type)"
                      class="w-5 h-5 text-slate-400"
                    />
                    <div class="flex-1">
                      <div class="text-sm text-slate-700 font-medium" v-html="highlightMatch(suggestion.text, searchWord)" />
                      <div v-if="suggestion.metadata?.breadcrumb" class="text-xs text-slate-400 mt-0.5">
                        {{ suggestion.metadata.breadcrumb }}
                      </div>
                    </div>
                    <div class="flex items-center gap-2">
                      <div v-if="suggestion.type !== 'KEYWORD'" class="text-xs text-slate-400 px-1.5 py-0.5 bg-slate-50 rounded">
                        {{ 
                          suggestion.type === 'BRAND' 
                            ? 'Marka' 
                            : suggestion.type === 'VENDOR'
                              ? 'Mağaza'
                              : 'Kategori' 
                        }}
                      </div>
                      <div v-else-if="suggestion.id === 'search-keyword'" class="text-xs text-slate-400 px-1.5 py-0.5 bg-slate-50 rounded">
                        Aranan
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
          
          <!-- Kategoriler -->
          <div v-if="productCategories.length > 0" class="mb-2">
            <div class="px-4 py-2 bg-slate-50 text-xs font-medium text-slate-500 uppercase">
              Kategoriler
            </div>
            <div class="divide-y divide-slate-100">
              <div
                v-for="(category, index) in productCategories"
                :key="category.id"
                :id="'category-' + index"
                :class="[
                  'hover:bg-slate-50 active:bg-slate-100 transition-colors',
                  { 'bg-slate-50': selectedIndex === (suggestions.length + index) }
                ]"
                role="option"
                :aria-selected="selectedIndex === (suggestions.length + index)"
              >
                <button
                  @click="navigateToSuggestion(category); closeSearch()"
                  class="w-full px-4 py-3 text-left"
                >
                  <div class="flex items-center gap-3">
                    <UIcon
                      :name="getIconForType(category.type)"
                      class="w-5 h-5 text-slate-400"
                    />
                    <div class="flex-1">
                      <div class="text-sm text-slate-700 font-medium" v-html="highlightMatch(category.text, searchWord)" />
                      <div v-if="category.metadata?.breadcrumb" class="text-xs text-slate-400 mt-0.5">
                        {{ category.metadata.breadcrumb }}
                      </div>
                    </div>
                    <div class="flex items-center gap-2">
                      <div class="text-xs text-slate-400 px-1.5 py-0.5 bg-slate-50 rounded">
                        Kategori
                      </div>
                      <div v-if="category.count" class="text-xs text-slate-400">
                        {{ category.count }} ürün
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
          
          <!-- Markalar -->
          <div v-if="productBrands.length > 0">
            <div class="px-4 py-2 bg-slate-50 text-xs font-medium text-slate-500 uppercase">
              Markalar
            </div>
            <div class="divide-y divide-slate-100">
              <div
                v-for="(brand, index) in productBrands"
                :key="brand.id"
                :id="'brand-' + index"
                :class="[
                  'hover:bg-slate-50 active:bg-slate-100 transition-colors',
                  { 'bg-slate-50': selectedIndex === (suggestions.length + productCategories.length + index) }
                ]"
                role="option"
                :aria-selected="selectedIndex === (suggestions.length + productCategories.length + index)"
              >
                <button
                  @click="navigateToSuggestion(brand); closeSearch()"
                  class="w-full px-4 py-3 text-left"
                >
                  <div class="flex items-center gap-3">
                    <div v-if="brand.metadata?.logo" class="w-8 h-8">
                      <NuxtImg
                        :src="'cl' + brand.metadata.logo"
                        class="w-full h-full object-contain border rounded-full"
                        :alt="brand.text"
                      />
                    </div>
                    <UIcon
                      v-else
                      :name="getIconForType(brand.type)"
                      class="w-5 h-5 text-slate-400"
                    />
                    <div class="flex-1">
                      <div class="text-sm text-slate-700 font-medium" v-html="highlightMatch(brand.text, searchWord)" />
                    </div>
                    <div class="flex items-center gap-2">
                      <div class="text-xs text-slate-400 px-1.5 py-0.5 bg-slate-50 rounded">
                        Marka
                      </div>
                      <div v-if="brand.count" class="text-xs text-slate-400">
                        {{ brand.count }} ürün
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="searchWord && !hasResults" class="flex flex-col items-center justify-center h-40 px-4 text-slate-500 text-sm">
          <UIcon name="i-heroicons-magnifying-glass" class="w-8 h-8 mb-2 text-slate-300" />
          <p>Sonuç bulunamadı</p>
        </div>

        <div v-else class="flex flex-col items-center justify-center h-40 px-4 text-slate-500 text-sm">
          <UIcon name="i-heroicons-magnifying-glass" class="w-8 h-8 mb-2 text-slate-300" />
          <p>Ne aramak istersiniz?</p>
        </div>
      </div>
    </div>

    <!-- Desktop Search -->
    <div v-else class="w-full relative z-[999]">
      <div class="relative w-full">
        <form @submit.prevent="goSearch" class="w-full">
          <UInput
            ref="searchInput"
            @focus="openSearch"
            @click="openSearch"
            @blur="handleBlur"
            @mousedown.stop
            type="text"
            @input="handleInput"
            @keydown="handleKeyDown"
            v-model="searchWord"
            size="lg"
            class="w-full"
            placeholder="Ürün, kategori veya marka ara..."
            role="combobox"
            aria-expanded="true"
            aria-controls="search-suggestions-desktop"
            :aria-activedescendant="selectedIndex >= 0 ? `suggestion-desktop-${selectedIndex}` : undefined"
          >
            <template #leading>
              <UIcon
                name="i-heroicons-magnifying-glass"
                class="w-5 h-5 text-slate-400/80"
              />
            </template>
            <template #trailing>
              <div class="flex items-center gap-2 pe-2">
                <UIcon
                  v-if="isLoading"
                  name="i-heroicons-arrow-path"
                  class="w-5 h-5 animate-spin text-orange-500"
                />
                <UIcon
                  v-else-if="searchWord"
                  name="i-heroicons-x-mark"
                  class="w-5 h-5 cursor-pointer text-slate-400/80 hover:text-slate-600 transition-colors"
                  @click="clearSearch"
                />
              </div>
            </template>
          </UInput>
        </form>

        <!-- Search Results Dropdown -->
        <Transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="transform scale-95 opacity-0"
          enter-to-class="transform scale-100 opacity-100"
          leave-active-class="transition duration-100 ease-in"
          leave-from-class="transform scale-100 opacity-100"
          leave-to-class="transform scale-95 opacity-0"
        >
          <div
            v-if="$mainState.isOpenSearch"
            id="search-suggestions-desktop"
            class="min-h-[20px] w-full p-0 bg-white absolute z-[999] left-0 right-0 top-[50px] rounded-xl shadow-lg border border-slate-200/80"
            role="listbox"
          >
            <div
              v-if="isLoading"
              class="text-slate-500 flex items-center justify-center gap-2 p-4"
            >
              <UIcon name="i-heroicons-arrow-path" class="w-4 h-4 animate-spin" />
              <span class="text-sm">Aranıyor...</span>
            </div>
            
            <div v-else-if="searchWord && hasResults">
              <!-- Öneriler -->
              <div v-if="suggestions.length > 0">
                <div class="px-4 py-2 bg-slate-50 text-xs font-medium text-slate-500 uppercase rounded-xl">
                  Öneriler
                </div>
                <div class="divide-y divide-slate-100">
                  <div
                    v-for="(suggestion, index) in suggestions.slice(0, 5)"
                    :key="suggestion.id"
                    :id="'suggestion-desktop-' + index"
                    role="option"
                    :aria-selected="selectedIndex === index"
                  >
                    <button
                      @click="navigateToSuggestion(suggestion); closeSearch()"
                      :class="[
                        'w-full text-left hover:text-orange-500 block py-3 px-4 text-slate-600 text-sm transition-colors',
                        { 'bg-slate-50': selectedIndex === index }
                      ]"
                    >
                      <div class="flex items-center gap-3">
                        <div v-if="(suggestion.type === 'BRAND' || suggestion.type === 'VENDOR') && suggestion.metadata?.logo" class="w-6 h-6">
                          <NuxtImg
                            :src="'cl' + suggestion.metadata.logo"
                            class="w-full h-full object-contain border rounded-full"
                            :alt="suggestion.text"
                          />
                        </div>
                        <UIcon
                          v-else
                          :name="getIconForType(suggestion.type)"
                          class="w-4 h-4 text-slate-400"
                        />
                        <div class="flex-1">
                          <div v-html="highlightMatch(suggestion.text, searchWord)" />
                          <div v-if="suggestion.metadata?.breadcrumb" class="text-xs text-slate-400 mt-0.5">
                            {{ suggestion.metadata.breadcrumb }}
                          </div>
                        </div>
                        <div class="flex items-center gap-2">
                          <div v-if="suggestion.type !== 'KEYWORD'" class="text-xs text-slate-400 px-1.5 py-0.5 bg-slate-50 rounded">
                            {{ 
                              suggestion.type === 'BRAND' 
                                ? 'Marka' 
                                : suggestion.type === 'VENDOR'
                                  ? 'Mağaza'
                                  : 'Kategori' 
                            }}
                          </div>
                          <div v-else-if="suggestion.id === 'search-keyword'" class="text-xs text-slate-400 px-1.5 py-0.5 bg-slate-50 rounded">
                            Aranan
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
              
              <!-- Kategoriler -->
              <div v-if="productCategories.length > 0">
                <div class="px-4 py-2 bg-slate-50 text-xs font-medium text-slate-500 uppercase">
                  Kategoriler
                </div>
                <div class="divide-y divide-slate-100">
                  <div
                    v-for="(category, index) in productCategories.slice(0, 3)"
                    :key="category.id"
                    :id="'category-desktop-' + index"
                    role="option"
                    :aria-selected="selectedIndex === (suggestions.length + index)"
                  >
                    <button
                      @click="navigateToSuggestion(category); closeSearch()"
                      :class="[
                        'w-full text-left hover:text-orange-500 block py-3 px-4 text-slate-600 text-sm transition-colors',
                        { 'bg-slate-50': selectedIndex === (suggestions.length + index) }
                      ]"
                    >
                      <div class="flex items-center gap-3">
                        <UIcon
                          :name="getIconForType(category.type)"
                          class="w-4 h-4 text-slate-400"
                        />
                        <div class="flex-1">
                          <div v-html="highlightMatch(category.text, searchWord)" />
                          <div v-if="category.metadata?.breadcrumb" class="text-xs text-slate-400 mt-0.5">
                            {{ category.metadata.breadcrumb }}
                          </div>
                        </div>
                        <div class="flex items-center gap-2">
                          <div class="text-xs text-slate-400 px-1.5 py-0.5 bg-slate-50 rounded">
                            Kategori
                          </div>
                          <div v-if="category.count" class="text-xs text-slate-400">
                            {{ category.count }} ürün
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
              
              <!-- Markalar -->
              <div v-if="productBrands.length > 0">
                <div class="px-4 py-2 bg-slate-50 text-xs font-medium text-slate-500 uppercase">
                  Markalar
                </div>
                <div class="divide-y divide-slate-100">
                  <div
                    v-for="(brand, index) in productBrands.slice(0, 3)"
                    :key="brand.id"
                    :id="'brand-desktop-' + index"
                    role="option"
                    :aria-selected="selectedIndex === (suggestions.length + productCategories.length + index)"
                  >
                    <button
                      @click="navigateToSuggestion(brand); closeSearch()"
                      :class="[
                        'w-full text-left hover:text-orange-500 block py-3 px-4 text-slate-600 text-sm transition-colors',
                        { 'bg-slate-50': selectedIndex === (suggestions.length + productCategories.length + index) }
                      ]"
                    >
                      <div class="flex items-center gap-3">
                        <div v-if="brand.metadata?.logo" class="w-6 h-6">
                          <NuxtImg
                            :src="'cl' + brand.metadata.logo"
                            class="w-full h-full object-contain border rounded-full"
                            :alt="brand.text"
                          />
                        </div>
                        <UIcon
                          v-else
                          :name="getIconForType(brand.type)"
                          class="w-4 h-4 text-slate-400"
                        />
                        <div class="flex-1">
                          <div v-html="highlightMatch(brand.text, searchWord)" />
                        </div>
                        <div class="flex items-center gap-2">
                          <div class="text-xs text-slate-400 px-1.5 py-0.5 bg-slate-50 rounded">
                            Marka
                          </div>
                          <div v-if="brand.count" class="text-xs text-slate-400">
                            {{ brand.count }} ürün
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <div v-else-if="searchWord && !hasResults" class="text-slate-500 text-sm p-4">
              Sonuç bulunamadı
            </div>
            
            <div v-else class="text-slate-500 text-sm p-4">
              Ne aramak istersiniz?
            </div>
          </div>
        </Transition>
      </div>
    </div>

    <!-- Desktop Overlay -->
    <div
      v-if="$mainState.isOpenSearch && !isMobile"
      class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[998]"
      @click="closeSearch"
    ></div>
  </div>
</template>

<script setup>


const { $mainState } = useNuxtApp()
const { isMobile } = useDevice()
const searchInput = ref(null)
const router = useRouter()
const route = useRoute()
const searchWord = ref(route.query.searchWord || '')
const lastQuery = ref('')

const {
  suggestions,
  productCategories,
  productBrands,
  isLoading,
  selectedIndex,
  search,
  handleKeyDown,
  reset: resetSearch,
  highlightMatch,
  getIconForType,
  navigateToSuggestion
} = useLiveSearch()

// Sonuçların olup olmadığını kontrol eden hesaplanmış özellik
const hasResults = computed(() => {
  return suggestions.value.length > 0 || 
         productCategories.value.length > 0 || 
         productBrands.value.length > 0
})

// Input odaklanma yardımcı fonksiyonu
const focusInput = (delay = 0) => {
  if (searchInput.value?.$refs?.input) {
    nextTick(() => {
      if (delay > 0) {
        setTimeout(() => {
          searchInput.value.$refs.input.focus()
        }, delay)
      } else {
        searchInput.value.$refs.input.focus()
      }
    })
  }
}

function clearSearch() {
  searchWord.value = ''
  lastQuery.value = ''
  resetSearch()
}

function closeSearch() {
  $mainState.isOpenSearch = false
  clearSearch()
  if (searchInput.value?.$refs?.input) {
    searchInput.value.$refs.input.blur()
  }
}

function goSearch() {
  if (!searchWord.value) return

  router.push({
    path: '/arama-a0',
    query: {
      searchWord: searchWord.value
    }
  })
  closeSearch()
}

function handleInput(evt) {
  const value = typeof evt === 'string' ? evt : evt?.target?.value || ''
  searchWord.value = value
  
  if (value.length > 0) {
    lastQuery.value = value
    search(value)
  } else {
    resetSearch()
  }
}

function handleBlur(event) {
  if (
    !event.relatedTarget ||
    !event.relatedTarget.closest('#search-suggestions-desktop')
  ) {
    setTimeout(() => {
      $mainState.isOpenSearch = false
    }, 200)
  }
}

function openSearch() {
  $mainState.isOpenSearch = true
  focusInput()
  
  // Eğer önceki bir arama varsa, sonuçları yeniden yükle
  if (lastQuery.value && lastQuery.value.length > 0) {
    searchWord.value = lastQuery.value
    search(lastQuery.value)
  }
}

// Sekme değişikliğini izleme
function handleVisibilityChange() {
  // Sekme değiştiğinde arama panelini kapat
  if (document.visibilityState === 'hidden') {
    $mainState.isOpenSearch = false
  }
}

// Mobil arama açıldığında input'a focus olmasını sağlayacak fonksiyon
function focusMobileSearchInput() {
  if (isMobile && $mainState.isOpenSearch) {
    focusInput(50) // Kısa bir gecikme ekleyerek DOM'un tamamen yüklenmesini sağlıyoruz
  }
}

// Event listener'ları eklemek için yardımcı fonksiyon
function addEventListeners() {
  // Masaüstü arama inputu için event listener ekle
  if (!isMobile && searchInput.value?.$refs?.input) {
    searchInput.value.$refs.input.addEventListener('click', openSearch)
    searchInput.value.$refs.input.addEventListener('focus', openSearch)
  }
  
  // Sekme değişikliği için event listener ekle
  if (process.client) {
    document.addEventListener('visibilitychange', handleVisibilityChange)
  }
}

// Event listener'ları temizlemek için yardımcı fonksiyon
function removeEventListeners() {
  if (!isMobile && searchInput.value?.$refs?.input) {
    searchInput.value.$refs.input.removeEventListener('click', openSearch)
    searchInput.value.$refs.input.removeEventListener('focus', openSearch)
  }
  
  if (process.client) {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  }
}

// $mainState.isOpenSearch değişimini izle
watch(() => $mainState.isOpenSearch, (newValue) => {
  if (newValue) {
    focusMobileSearchInput()
  }
})

onMounted(() => {
  addEventListeners()
  focusMobileSearchInput()
})

onBeforeUnmount(() => {
  removeEventListeners()
})
</script>
<style scoped>
.results-container {
  box-shadow:
    0 4px 6px -1px rgb(0 0 0 / 0.1),
    0 2px 4px -2px rgb(0 0 0 / 0.1);
}
</style>
